<template>
  <div class="data-table-pagination">
    <div class="pagination-right">
      <span v-if="showTotal" class="pagination-total">共 {{ total }} 条记录</span>
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="currentPageSize"
        :total="total"
        layout="prev, pager, next"
        class="pagination-controls"
        @current-change="(p: number) => emit('pageChange', p)"
      />
      <el-select
        v-model="currentPageSize"
        placement="top"
        class="pagination-size-select"
        style="width: 88px"
        popper-class="pagination-size-popper"
        @change="(s: number) => emit('sizeChange', s)"
      >
        <el-option v-for="s in pageSizes" :key="s" :value="s" :label="`${s}条/页`" />
      </el-select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PAGE_SIZE_OPTIONS } from '@/constants/api';

const props = withDefaults(
  defineProps<{
    page: number;
    pageSize: number;
    total: number;
    pageSizes?: number[];
    /** 是否显示总条数；游标分页接口不返回总数，此时应关闭避免展示推算值 */
    showTotal?: boolean;
  }>(),
  {
    pageSizes: () => PAGE_SIZE_OPTIONS,
    showTotal: true,
  },
);

const emit = defineEmits<{
  'update:page': [value: number];
  'update:pageSize': [value: number];
  pageChange: [page: number];
  sizeChange: [size: number];
}>();

const currentPage = computed({
  get: () => props.page,
  set: (v) => emit('update:page', v),
});

const currentPageSize = computed({
  get: () => props.pageSize,
  set: (v) => emit('update:pageSize', v),
});
</script>

<style lang="scss" scoped>
.data-table-pagination {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 16px;

  .pagination-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .pagination-total {
      font-size: 14px;
      color: #606266;
    }

    .pagination-size-select {
      width: 100px !important;

      :deep(.el-select__wrapper) {
        min-height: 28px;
        height: 28px;
        padding: 0 8px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        box-shadow: none;

        .el-select__selected-item {
          color: #606266;
        }
      }
    }

    :deep(.pagination-controls) {
      display: flex;
      align-items: center;
      gap: 8px;

      .btn-prev,
      .btn-next {
        min-width: 28px;
        height: 28px;
        padding: 0 6px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        color: #606266;

        &:hover:not(:disabled) {
          background: #ecf5ff;
          border-color: var(--el-color-primary);
          color: var(--el-color-primary);
        }

        &:disabled {
          background: #f5f7fa;
          color: #c0c4cc;
        }
      }

      .el-pager {
        margin: 0;
        gap: 4px;

        li {
          min-width: 28px;
          height: 28px;
          line-height: 26px;
          margin: 0;
          background: #f5f7fa;
          border: 1px solid #e4e7ed;
          border-radius: 4px;
          color: #606266;

          &:hover {
            background: #ecf5ff;
            border-color: var(--el-color-primary);
            color: var(--el-color-primary);
          }

          &.is-active {
            background: #fff;
            border-color: var(--el-color-primary);
            color: var(--el-color-primary);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.pagination-size-popper {
  min-width: 88px !important;
}
</style>
